/** Home: the map, "Where to?", saved places and any trip that's under way. */

import { get } from "@taxi/mobile-web/api";
import { minutes, when } from "@taxi/mobile-web/format";
import { useHeight, useInterval, useMyLocation, useNow, useResource } from "@taxi/mobile-web/hooks";
import {
  BriefcaseIcon,
  ChevronRight,
  ClockIcon,
  HomeIcon,
  LocateIcon,
  PlusIcon,
  SearchIcon,
  StarIcon,
} from "@taxi/mobile-web/icons";
import { MapView } from "@taxi/mobile-web/map";
import { useRealtime } from "@taxi/mobile-web/realtime";
import { navigate } from "@taxi/mobile-web/router";
import { IconButton, Plate } from "@taxi/mobile-web/ui";
import brand from "../../../../brand/brand.json";
import logo from "../../../../brand/logo.svg";
import { placeSubtitle, placeTitle, recentPlaces, useDraft } from "../lib/draft";
import { useMe } from "../lib/session";
import type { Booking, Page, Place, SavedPlace } from "../lib/types";

const LIVE = ["pending_payment", "dispatching", "offered", "accepted", "arrived", "on_board"];

function greeting(now: Date): string {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      hour: "numeric",
      hourCycle: "h23",
    }).format(now),
  );
  return hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
}

const placeFromSaved = (p: SavedPlace): Place => ({
  address: p.address,
  postcode: p.postcode,
  lat: p.lat,
  lng: p.lng,
  name: p.label,
});

const near = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) =>
  Math.abs(a.lat - b.lat) < 2e-4 && Math.abs(a.lng - b.lng) < 2e-4;

export function HomeScreen() {
  const me = useMe();
  const now = useNow();
  const { reset } = useDraft();
  const { location, locate } = useMyLocation();
  const [sheet, sheetHeight] = useHeight<HTMLDivElement>();
  const places = useResource(() => get<{ data: SavedPlace[] }>("/customer/places"), []);
  const upcoming = useResource(
    () => get<Page<Booking>>("/customer/bookings?when=upcoming&limit=5"),
    [],
  );
  useRealtime((event) => {
    if (
      event.type === "job.updated" ||
      event.type === "job.created" ||
      event.type === "reconnected"
    )
      void upcoming.reload();
  });
  useInterval(() => void upcoming.reload(), 60_000);

  const live = upcoming.data?.data.find((b) => LIVE.includes(b.status));
  const next = upcoming.data?.data.find((b) => b.status === "scheduled");
  const here = location.status === "known" ? location.at : null;

  function startTo(dropoff: Place | null) {
    reset({ dropoff });
    navigate("/book");
  }

  const saved = places.data?.data ?? [];
  const shortcuts = [
    ...saved.filter((p) => p.kind === "home"),
    ...saved.filter((p) => p.kind === "work"),
    ...saved.filter((p) => p.kind === "other"),
  ];
  // Somewhere they've been lately that isn't saved: one tap to go again.
  const recent = recentPlaces()
    .filter((place) => !saved.some((s) => near(s, place)))
    .slice(0, 2);

  return (
    <div className="home">
      <MapView me={here} className="home-map" bottomInset={sheetHeight || 340} />
      <div className="home-top">
        <img src={logo} alt={brand.firmName} className="home-logo" />
        <IconButton
          label="Show where I am"
          className="floating"
          onClick={() => void locate()}
          aria-pressed={location.status === "known"}
        >
          <LocateIcon size={20} />
        </IconButton>
      </div>
      <div className="home-sheet" ref={sheet}>
        <div className="sheet-grabber" aria-hidden="true" />
        {live && <LiveTrip booking={live} />}
        <div className="home-hello">
          <p>
            {greeting(now)}, {me.name.split(" ")[0]}
          </p>
          <h1>Where are you going?</h1>
        </div>
        <button type="button" className="where-to" onClick={() => startTo(null)}>
          <SearchIcon size={22} />
          <span className="where-to-text">Where to?</span>
          <span className="where-to-when">
            <ClockIcon size={15} /> Now
          </span>
        </button>
        {places.data && (
          <div className="shortcuts">
            {shortcuts.map((place) => (
              <button
                type="button"
                key={place.id}
                className="shortcut"
                onClick={() => startTo(placeFromSaved(place))}
              >
                <span className="shortcut-icon">
                  {place.kind === "home" ? (
                    <HomeIcon size={18} />
                  ) : place.kind === "work" ? (
                    <BriefcaseIcon size={18} />
                  ) : (
                    <StarIcon size={18} />
                  )}
                </span>
                <strong>{place.label}</strong>
                <span>{place.address.split(",")[0]}</span>
              </button>
            ))}
            <button
              type="button"
              className="shortcut shortcut-add"
              onClick={() => navigate("/account/places")}
            >
              <span className="shortcut-icon">
                <PlusIcon size={18} />
              </span>
              <strong>{shortcuts.length ? "Add" : "Save Home"}</strong>
              <span>{shortcuts.length ? "a place" : "and Work"}</span>
            </button>
          </div>
        )}
        {recent.length > 0 && (
          <ul className="recent">
            {recent.map((place) => (
              <li key={`${place.lat},${place.lng}`}>
                <button type="button" className="recent-place" onClick={() => startTo(place)}>
                  <span className="recent-icon">
                    <ClockIcon size={18} />
                  </span>
                  <span className="recent-text">
                    <strong>{placeTitle(place)}</strong>
                    <span>{placeSubtitle(place)}</span>
                  </span>
                  <ChevronRight size={18} />
                </button>
              </li>
            ))}
          </ul>
        )}
        {!live && next && (
          <button
            type="button"
            className="next-booking"
            onClick={() => navigate(`/bookings/${next.id}`)}
          >
            <span className="next-booking-label">Booked</span>
            <span className="next-booking-text">
              <strong>{when(new Date(next.pickupAt), now)}</strong>
              <span>To {placeTitle(next.dropoff)}</span>
            </span>
            <ChevronRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

function LiveTrip({ booking }: { booking: Booking }) {
  const driver = booking.driver?.name.split(" ")[0] ?? "Your driver";
  const eta = booking.status === "accepted" ? booking.driver?.etaSeconds : null;
  const car = booking.driver?.vehicle;
  const headline =
    booking.status === "accepted"
      ? `${driver} is on the way`
      : booking.status === "arrived"
        ? `${driver} is here`
        : booking.status === "on_board"
          ? `On the way to ${placeTitle(booking.dropoff)}`
          : booking.status === "pending_payment"
            ? "Finish paying to book"
            : "Finding you a driver";
  return (
    <button
      type="button"
      className={`live-trip live-${booking.status}`}
      onClick={() => navigate(`/bookings/${booking.id}`)}
    >
      <span className="live-trip-top">
        <span className="live-dot" />
        <span>Booking {booking.number}</span>
        <ChevronRight size={16} />
      </span>
      <span className="live-trip-main">
        <strong>{headline}</strong>
        {eta ? (
          <span className="live-eta">
            {Math.max(1, Math.round(eta / 60))}
            <small>min</small>
          </span>
        ) : null}
      </span>
      {car && ["accepted", "arrived"].includes(booking.status) && (
        <span className="live-trip-car">
          <span>
            {car.colour} {car.make} {car.model}
          </span>
          <Plate registration={car.registration} />
        </span>
      )}
      {booking.status === "on_board" && booking.driver?.etaSeconds ? (
        <span className="live-trip-car">About {minutes(booking.driver.etaSeconds)} to go</span>
      ) : null}
    </button>
  );
}
